/**
 * useOwnBlankGroups (BLU-1522 S8): the draft and the save for putting some of
 * a product's colours (or sizes) on other of the org's OWN base garments.
 *
 * An own base garment is one of the org's product type CODES (its own code
 * table, `settings.productTypes`), named `CODE · store names` from that same
 * table. The product's own type code is the default garment: every value no
 * group takes. The draft is S2's shape (`split`, up to five groups of store
 * values), moved by the same pure helper the 3PL composer uses
 * (`lib/garment-groups`).
 *
 * The save is `productSync.saveOwnBlankGroups`, once per click (a click while
 * one is in flight is ignored). Every refusal comes back as data and shows as
 * one inline line; a thrown error is `save_uncertain` when the answer never
 * arrived, else `save_failed`. A success invalidates the preview page so the
 * row re-reads its `ownBlankGroups`. Nothing is written to Shopify; the next
 * Sync selected run builds the print SKUs.
 *
 * Synced variants (`product.syncedVariantIds`) are locked: their colour (or
 * size) cannot be ticked or moved. The scan sends that list only for an org
 * that already keeps groups, so a first save on a partly synced product
 * relies on the server: an `already_synced` answer names the variants, which
 * lock here and go back to their saved garment in the draft.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  nameListIncludes,
  ownBlankCodeFor,
  ownCodeIsSingleSize,
  productTypeCodeValues,
  type ProductSyncSettings,
} from '@threadheads/shared';
import { trpc, type RouterInputs, type RouterOutputs } from '@/lib/trpc';
import { isUncertainOutcomeError, trpcErrorCode } from '@/lib/trpc-error-utils';
import {
  GARMENT_GROUPS_MAX,
  garmentValueKey,
  moveValuesToGarment,
  productValues,
  type GarmentSplit,
} from '../lib/garment-groups';
import type { PreviewProduct } from '../lib/types';

type SaveInput = RouterInputs['productSync']['saveOwnBlankGroups'];
type SaveResult = RouterOutputs['productSync']['saveOwnBlankGroups'];
type SaveRefusalResult = Extract<SaveResult, { ok: false }>;

/** A product's saved entry as the preview sends it (`{ invalid: true }` for one that does not read). */
export type OwnBlankSavedEntry = NonNullable<PreviewProduct['ownBlankGroups']>;
/** A saved entry that reads. */
export type OwnBlankValidEntry = Exclude<OwnBlankSavedEntry, { invalid: true }>;

/** Every refusal the save can show: the server's own, plus the two a thrown error maps to. */
export type OwnBlankRefusalCode = SaveRefusalResult['refusal'] | 'save_uncertain' | 'rate_limited';
export interface OwnBlankRefusal {
  refusal: OwnBlankRefusalCode;
  garmentKey?: string;
  /** `already_synced`: the synced colours or sizes went back to their saved garment. */
  restored?: boolean;
}

/** One of the org's own product type codes, as a base garment. */
export interface OwnGarmentOption {
  /** Upper-case product type code. */
  code: string;
  /** The org's own product type names mapped to it, joined (`BlankValueOption.name`). */
  name: string | null;
  /** `CODE · names`, the row's Base garment text. */
  title: string;
}

export interface OwnBlankGroupDraft {
  /** Upper-case product type code. */
  garmentKey: string;
  /** The store values on this garment, as the product names them. */
  storeValues: string[];
}

export interface OwnBlankDraft {
  split: GarmentSplit | null;
  groups: OwnBlankGroupDraft[];
}

type ShapeProduct = Pick<PreviewProduct, 'productType' | 'matchedOptions'>;

/** The product has no size segment: a single-size type, or no size option (plan.ts's `omitSize`). */
function omitsSize(product: ShapeProduct, settings: Pick<ProductSyncSettings, 'singleSizeTypes'>): boolean {
  const typeName = product.productType.name ?? '';
  return (
    (typeName !== '' && nameListIncludes(settings.singleSizeTypes ?? [], typeName)) || product.matchedOptions.size === null
  );
}

/**
 * The org's own product type codes this product can use as a base garment,
 * each with the org's own names for it (CLAUDE.md: code-keyed settings show
 * the org's own names). The default code (the product's own type) comes first
 * and is always listed, so a value can move back to it; another code is
 * listed only when its blank has the same shape (sizes or one size), the
 * server's `garment_shape_differs` rule. Read from the SAVED settings, the
 * doc the save checks codes against.
 */
export function ownGarmentOptions(doc: ProductSyncSettings, product: ShapeProduct): OwnGarmentOption[] {
  // A doc that lacks a table reads as an empty one (never throws).
  const settings = { productTypes: doc.productTypes ?? {}, singleSizeTypes: doc.singleSizeTypes ?? [] };
  const valid = productTypeCodeValues(settings);
  const names = new Map<string, string[]>();
  for (const [name, value] of Object.entries(settings.productTypes)) {
    if (typeof value !== 'string') continue;
    const code = value.trim().toUpperCase();
    if (!valid.has(code)) continue;
    names.set(code, [...(names.get(code) ?? []), name]);
  }
  const defaultCode = product.productType.code?.trim().toUpperCase() ?? '';
  const omitSize = omitsSize(product, settings);
  const option = (code: string): OwnGarmentOption => {
    const list = names.get(code) ?? [];
    const name = list.length > 0 ? list.join(', ') : null;
    return { code, name, title: name ? `${code} · ${name}` : code };
  };
  const others = [...names.keys()]
    .filter((code) => code !== defaultCode && ownCodeIsSingleSize(code, settings) === omitSize)
    .sort((a, b) => a.localeCompare(b))
    .map(option);
  return defaultCode ? [option(defaultCode), ...others] : others;
}

/** Which options the editor offers to split by: one the product has more than one value of. */
export function ownSplittable(
  product: Pick<PreviewProduct, 'variants' | 'productType' | 'matchedOptions'>,
  settings: Pick<ProductSyncSettings, 'singleSizeTypes'>,
): { colour: boolean; size: boolean } {
  return {
    colour: product.matchedOptions.colour !== null && productValues(product, 'colour').length > 1,
    size: !omitsSize(product, settings) && productValues(product, 'size').length > 1,
  };
}

/** Whether a split still exists on the product (the plan's rule: a colour option; sizes for a size split). */
function splitAvailable(product: ShapeProduct, settings: Pick<ProductSyncSettings, 'singleSizeTypes'>, split: GarmentSplit) {
  return split === 'colour' ? product.matchedOptions.colour !== null : !omitsSize(product, settings);
}

export function isValidOwnEntry(entry: OwnBlankSavedEntry | null | undefined): entry is OwnBlankValidEntry {
  return entry != null && !('invalid' in entry);
}

/**
 * A saved entry the plan holds back (`own_blank_groups_changed`): it does not
 * read, its split is gone, a value is on no variant any more, or a code is no
 * longer one this product can use.
 */
export function isOwnEntryStale(
  entry: OwnBlankSavedEntry | null | undefined,
  product: Pick<PreviewProduct, 'variants' | 'productType' | 'matchedOptions'>,
  settings: ProductSyncSettings,
  options: readonly OwnGarmentOption[],
): boolean {
  if (entry == null) return false;
  if (!isValidOwnEntry(entry)) return true;
  if (!splitAvailable(product, settings, entry.split)) return true;
  const present = new Set(productValues(product, entry.split).map((v) => garmentValueKey(v.value)));
  const defaultCode = product.productType.code?.trim().toUpperCase() ?? '';
  const usable = new Set(options.map((o) => o.code).filter((code) => code !== defaultCode));
  return entry.groups.some(
    (group) => !usable.has(group.code.toUpperCase()) || group.values.some((value) => !present.has(garmentValueKey(value))),
  );
}

/**
 * A saved entry as a draft: each group on a code this product can still use,
 * its values in the product's own spelling; a value on no variant any more
 * and a group left empty are dropped. Nothing saved (or an entry that does
 * not read): one base garment, split by `initialSplit` when given.
 */
export function ownDraftFromEntry(
  entry: OwnBlankSavedEntry | null | undefined,
  product: Pick<PreviewProduct, 'variants' | 'productType' | 'matchedOptions'>,
  settings: ProductSyncSettings,
  options: readonly OwnGarmentOption[],
  initialSplit: GarmentSplit | null = null,
): OwnBlankDraft {
  if (!isValidOwnEntry(entry) || !splitAvailable(product, settings, entry.split)) {
    return { split: initialSplit, groups: [] };
  }
  const spelling = new Map(productValues(product, entry.split).map((v) => [garmentValueKey(v.value), v.value]));
  const defaultCode = product.productType.code?.trim().toUpperCase() ?? '';
  const usable = new Set(options.map((o) => o.code).filter((code) => code !== defaultCode));
  const groups = entry.groups
    .map((group) => ({
      garmentKey: group.code.toUpperCase(),
      storeValues: group.values
        .map((value) => spelling.get(garmentValueKey(value)))
        .filter((value): value is string => typeof value === 'string'),
    }))
    .filter((group) => usable.has(group.garmentKey) && group.storeValues.length > 0)
    .slice(0, GARMENT_GROUPS_MAX);
  return { split: entry.split, groups };
}

function canonicalOf(split: GarmentSplit | null, groups: Array<{ code: string; values: readonly string[] }>): string {
  const pairs = split
    ? groups
        .map((group) => ({ code: group.code.toUpperCase(), values: group.values.map(garmentValueKey).sort() }))
        .filter((group) => group.values.length > 0)
        .sort((a, b) => a.code.localeCompare(b.code))
    : [];
  return pairs.length > 0 ? JSON.stringify({ split, groups: pairs }) : 'none';
}

/** One comparable string per draft, so "changed since saved" is a string compare (`ownEntryKey`). */
export function ownDraftKey(draft: OwnBlankDraft): string {
  return canonicalOf(
    draft.split,
    draft.groups.map((group) => ({ code: group.garmentKey, values: group.storeValues })),
  );
}

/** A saved entry's comparable string: 'none' for nothing saved, 'invalid' for one that does not read. */
export function ownEntryKey(entry: OwnBlankSavedEntry | null | undefined): string {
  if (entry == null) return 'none';
  if (!isValidOwnEntry(entry)) return 'invalid';
  return canonicalOf(entry.split, entry.groups);
}

/**
 * The synced variants: the scan's list, else every variant of a fully synced
 * product (failing closed), plus any the server named in an `already_synced`
 * answer.
 */
export function ownSyncedVariantIds(
  product: Pick<PreviewProduct, 'variants' | 'variantCount' | 'syncedVariantCount' | 'syncedVariantIds'>,
  extra: ReadonlySet<string> = new Set(),
): Set<string> {
  const listed = product.syncedVariantIds;
  const ids = new Set<string>(
    Array.isArray(listed)
      ? listed
      : product.variantCount > 0 && product.syncedVariantCount >= product.variantCount
        ? product.variants.map((v) => v.id)
        : [],
  );
  for (const id of extra) ids.add(id);
  return ids;
}

/** The store values (keys) of the split option that carry a synced variant: those rows are locked. */
export function ownLockedValues(
  product: Pick<PreviewProduct, 'variants'>,
  split: GarmentSplit,
  synced: ReadonlySet<string>,
): Set<string> {
  const out = new Set<string>();
  for (const variant of product.variants) {
    if (!synced.has(variant.id)) continue;
    const key = garmentValueKey(split === 'colour' ? variant.colourName : variant.sizeName);
    if (key) out.add(key);
  }
  return out;
}

/**
 * The split cannot change or close while a synced variant sits on a base
 * garment other than the default (changing it would move that variant).
 */
export function isOwnSplitLocked(
  entry: OwnBlankSavedEntry | null | undefined,
  product: Pick<PreviewProduct, 'variants'>,
  synced: ReadonlySet<string>,
): boolean {
  if (!isValidOwnEntry(entry)) return false;
  return product.variants.some(
    (variant) =>
      synced.has(variant.id) && ownBlankCodeFor(entry, variant.colourName ?? null, variant.sizeName ?? null) !== null,
  );
}

/** The plain words for a refusal; `garment` names the refused code (`CODE · names`). */
export function ownRefusalMessage(
  t: TFunction<'productSync'>,
  refusal: OwnBlankRefusal,
  context: { split: GarmentSplit | null; garment?: string },
): string {
  const code = context.garment ?? refusal.garmentKey ?? '';
  switch (refusal.refusal) {
    case 'product_not_found':
      return t('ownBlanks.refusal.productNotFound');
    case 'too_many_variants':
      return t('ownBlanks.refusal.tooManyVariants');
    case 'byo_not_supported':
      return t('job.blocker.own_blank_groups_byo');
    case 'product_type_unmapped':
      return t('ownBlanks.refusal.typeUnmapped');
    case 'split_unavailable':
      return context.split === 'size'
        ? t('supply.refusal.splitUnavailableSize')
        : context.split === 'colour'
          ? t('supply.refusal.splitUnavailableColour')
          : t('supply.refusal.splitUnavailable');
    case 'garment_not_in_code_table':
      return t('ownBlanks.refusal.notInCodeTable', { code });
    case 'garment_repeated':
      return t('ownBlanks.refusal.repeated', { code });
    case 'value_not_on_product':
      return t('ownBlanks.refusal.valueGone');
    case 'garment_shape_differs':
      return t('ownBlanks.refusal.shapeDiffers', { code });
    case 'settings_missing':
      return t('ownBlanks.refusal.settingsMissing');
    case 'supply_elected':
      return t('ownBlanks.refusal.supplyElected');
    case 'already_synced':
      return refusal.restored ? t('ownBlanks.refusal.alreadySynced') : t('ownBlanks.refusal.alreadySyncedUnknown');
    case 'full':
      return t('ownBlanks.refusal.full');
    case 'save_uncertain':
      return t('ownBlanks.refusal.saveUncertain');
    case 'rate_limited':
      return t('ownBlanks.refusal.rateLimited');
    default:
      return t('ownBlanks.refusal.saveFailed');
  }
}

/** What a save answered, for the panel: the entry now saved (null = one base garment). */
export interface OwnBlankSaved {
  entry: OwnBlankValidEntry | null;
  changed: boolean;
}

export interface UseOwnBlankGroupsOptions {
  /** The saved settings doc: the code table the save checks against. */
  settings: ProductSyncSettings;
  /** The product's saved entry (the panel's view of it, ahead of the preview refetch). */
  savedEntry: OwnBlankSavedEntry | undefined;
  /** Nothing saved yet: the split the editor opens with (the link's choice). */
  initialSplit?: GarmentSplit | null;
  onSaved?: (saved: OwnBlankSaved) => void;
}

export type OwnBlankAssignOutcome = 'assigned' | 'too_many';

export interface UseOwnBlankGroupsResult {
  draft: OwnBlankDraft;
  options: OwnGarmentOption[];
  defaultCode: string;
  /** Per colour, per size, or (null) one base garment for all; changing it clears the groups. */
  setSplit: (split: GarmentSplit | null) => void;
  /** Put these store values on a code (the default code takes them back). Locked values never move. */
  assign: (storeValues: string[], code: string) => OwnBlankAssignOutcome;
  /** Store value keys of the split option with a synced variant. */
  lockedValues: ReadonlySet<string>;
  splitLocked: boolean;
  /** The draft differs from what is saved. */
  dirty: boolean;
  save: () => Promise<void>;
  /** Save one base garment for all, whatever the draft (the BYO clear-only panel). */
  clear: () => Promise<void>;
  saving: boolean;
  refusal: OwnBlankRefusal | null;
  refusalMessage: string | null;
}

export function useOwnBlankGroups(product: PreviewProduct, options: UseOwnBlankGroupsOptions): UseOwnBlankGroupsResult {
  const { t } = useTranslation('productSync');
  const { settings, savedEntry, initialSplit = null } = options;
  const garmentOptions = useMemo(() => ownGarmentOptions(settings, product), [settings, product]);
  const defaultCode = product.productType.code?.trim().toUpperCase() ?? '';
  const [draft, setDraft] = useState<OwnBlankDraft>(() =>
    ownDraftFromEntry(savedEntry, product, settings, garmentOptions, initialSplit),
  );
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const [extraSynced, setExtraSynced] = useState<ReadonlySet<string>>(() => new Set());
  const [saving, setSaving] = useState(false);
  const [outcome, setOutcome] = useState<{ key: string; refusal: OwnBlankRefusal } | null>(null);

  const utils = trpc.useUtils();
  const mutation = trpc.productSync.saveOwnBlankGroups.useMutation();
  const mutateRef = useRef(mutation.mutateAsync);
  mutateRef.current = mutation.mutateAsync;
  const onSavedRef = useRef(options.onSaved);
  onSavedRef.current = options.onSaved;
  const savingRef = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const synced = useMemo(() => ownSyncedVariantIds(product, extraSynced), [product, extraSynced]);
  const lockedValues = useMemo(
    () => (draft.split ? ownLockedValues(product, draft.split, synced) : new Set<string>()),
    [product, draft.split, synced],
  );
  const lockedRef = useRef(lockedValues);
  lockedRef.current = lockedValues;
  const splitLocked = isOwnSplitLocked(savedEntry, product, synced);
  const splitLockedRef = useRef(splitLocked);
  splitLockedRef.current = splitLocked;

  const setSplit = useCallback((split: GarmentSplit | null) => {
    setDraft((current) => {
      if (current.split === split) return current;
      if (splitLockedRef.current) return current;
      return { split, groups: [] };
    });
  }, []);

  const assign = useCallback(
    (picked: string[], code: string): OwnBlankAssignOutcome => {
      const current = draftRef.current;
      if (!current.split) return 'assigned';
      const storeValues = picked.filter((value) => !lockedRef.current.has(garmentValueKey(value)));
      if (storeValues.length === 0) return 'assigned';
      const key = code.trim().toUpperCase();
      const moved = moveValuesToGarment(
        current.groups,
        storeValues,
        { key: key === defaultCode ? null : key, create: (values) => ({ garmentKey: key, storeValues: values }) },
        { keyOf: (group) => group.garmentKey },
      );
      if (moved.outcome === 'too_many') return 'too_many';
      const next = { split: current.split, groups: moved.groups };
      draftRef.current = next;
      setDraft(next);
      return 'assigned';
    },
    [defaultCode],
  );

  const draftKey = ownDraftKey(draft);
  const savedKey = ownEntryKey(savedEntry);
  const dirty = draftKey !== savedKey;

  const saveDraft = useCallback(async (current: OwnBlankDraft) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const key = ownDraftKey(current);
    const input: SaveInput =
      current.split && current.groups.length > 0
        ? {
            productId: product.id,
            split: current.split,
            groups: current.groups.map((group) => ({ garmentKey: group.garmentKey, storeValues: [...group.storeValues] })),
          }
        : { productId: product.id, groups: [] };
    try {
      const result = await mutateRef.current(input);
      if (!mounted.current) return;
      if (result.ok) {
        setOutcome(null);
        void utils.productSync.getProductSyncPreview.invalidate();
        onSavedRef.current?.({ entry: (result.ownBlankGroups as OwnBlankValidEntry | null) ?? null, changed: result.changed });
        return;
      }
      const ids = result.refusal === 'already_synced' ? (result.variantIds ?? []) : [];
      if (ids.length > 0 && current.split) {
        // The named variants lock, and their values go back to the garment they were saved on.
        const split = current.split;
        const nowSynced = new Set(ids);
        setExtraSynced((prev) => new Set([...prev, ...ids]));
        const restored = restoreSynced(current, split, product, nowSynced, savedEntry, defaultCode);
        draftRef.current = restored;
        setDraft(restored);
        setOutcome({ key: ownDraftKey(restored), refusal: { refusal: 'already_synced', restored: true } });
        return;
      }
      setOutcome({
        key,
        refusal: result.garmentKey ? { refusal: result.refusal, garmentKey: result.garmentKey } : { refusal: result.refusal },
      });
    } catch (error) {
      if (!mounted.current) return;
      const refusal: OwnBlankRefusalCode = isUncertainOutcomeError(error)
        ? 'save_uncertain'
        : trpcErrorCode(error) === 'TOO_MANY_REQUESTS'
          ? 'rate_limited'
          : 'save_failed';
      setOutcome({ key, refusal: { refusal } });
    } finally {
      savingRef.current = false;
      if (mounted.current) setSaving(false);
    }
  }, [product, savedEntry, defaultCode, utils]);

  const save = useCallback(() => saveDraft(draftRef.current), [saveDraft]);
  const clear = useCallback(() => {
    if (savingRef.current) return Promise.resolve();
    // The draft becomes the clear, so a refusal is shown against it.
    const next: OwnBlankDraft = { split: null, groups: [] };
    draftRef.current = next;
    setDraft(next);
    return saveDraft(next);
  }, [saveDraft]);

  // A refusal belongs to the draft it was sent from; any change clears it.
  const refusal = outcome && outcome.key === draftKey ? outcome.refusal : null;
  const refusedGarment = refusal?.garmentKey
    ? garmentOptions.find((o) => o.code === refusal.garmentKey?.toUpperCase())?.title
    : undefined;
  const refusalMessage = refusal
    ? ownRefusalMessage(t, refusal, { split: draft.split, ...(refusedGarment ? { garment: refusedGarment } : {}) })
    : null;

  return {
    draft,
    options: garmentOptions,
    defaultCode,
    setSplit,
    assign,
    lockedValues,
    splitLocked,
    dirty,
    save,
    clear,
    saving,
    refusal,
    refusalMessage,
  };
}

/** The draft with every value that carries one of `nowSynced` back on the garment it was saved on. */
function restoreSynced(
  draft: OwnBlankDraft,
  split: GarmentSplit,
  product: Pick<PreviewProduct, 'variants'>,
  nowSynced: ReadonlySet<string>,
  savedEntry: OwnBlankSavedEntry | undefined,
  defaultCode: string,
): OwnBlankDraft {
  let groups = draft.groups;
  const seen = new Set<string>();
  for (const variant of product.variants) {
    if (!nowSynced.has(variant.id)) continue;
    const value = split === 'colour' ? variant.colourName : variant.sizeName;
    const key = garmentValueKey(value);
    if (!key || seen.has(key) || typeof value !== 'string') continue;
    seen.add(key);
    // The garment it was saved on: its group under the same split, else the default.
    const savedCode =
      isValidOwnEntry(savedEntry) && savedEntry.split === split
        ? (savedEntry.groups.find((g) => g.values.some((v) => garmentValueKey(v) === key))?.code.toUpperCase() ?? null)
        : null;
    const target = savedCode && savedCode !== defaultCode ? savedCode : null;
    const moved = moveValuesToGarment(
      groups,
      [value.trim()],
      { key: target, create: (values) => ({ garmentKey: target ?? '', storeValues: values }) },
      { keyOf: (group) => group.garmentKey, max: Number.POSITIVE_INFINITY },
    );
    if (moved.outcome === 'assigned') groups = moved.groups;
  }
  return { split, groups };
}
